import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { KeyRound } from "lucide-react";
import { LoginInput } from "@ndis/contracts";
import { api, setToken } from "../lib/api";
import {
  passkeyErrorMessage, passkeysEnabled, passkeysSupported, signInWithPasskey,
} from "../lib/passkey";
import AuthShell from "../components/AuthShell";

export default function Login() {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [passkeyReady, setPasskeyReady] = useState(false);

  // Offer the passkey button only when both halves are real: a browser that
  // implements WebAuthn and a server with a relying party configured.
  useEffect(() => {
    if (passkeysSupported()) void passkeysEnabled().then(setPasskeyReady);
  }, []);

  async function passkeySignIn() {
    setError(null);
    setBusy(true);
    try {
      // The email is a hint only — leave it blank and the browser offers
      // whichever passkey it holds for this site.
      const { token } = await signInWithPasskey(email.trim() || undefined);
      setToken(token);
      navigate("/");
    } catch (e) {
      setError(passkeyErrorMessage(e));
    } finally {
      setBusy(false);
    }
  }

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    const parsed = LoginInput.safeParse({ email, password });
    if (!parsed.success) { setError("Enter a valid email and password"); return; }
    setBusy(true);
    try {
      const { token } = await api<{ token: string }>("/v1/auth/login", {
        method: "POST", body: JSON.stringify(parsed.data),
      });
      setToken(token);
      navigate("/");
    } catch {
      setError("Invalid credentials");
    } finally {
      setBusy(false);
    }
  }

  return (
    <AuthShell>
      <form onSubmit={submit} className="w-full max-w-sm card p-8 space-y-5">
        <div>
          <h2 className="text-xl font-semibold">Welcome back</h2>
          <p className="text-sm text-mut mt-1">Sign in to your organisation</p>
        </div>
        <label className="block">
          <span className="label">Email</span>
          <input type="email" value={email} onChange={(e) => setEmail(e.target.value)}
            autoComplete="email" placeholder="you@organisation.com.au" className="input" />
        </label>
        <label className="block">
          <span className="label">Password</span>
          <input type="password" value={password} onChange={(e) => setPassword(e.target.value)}
            autoComplete="current-password" placeholder="••••••••••" className="input" />
        </label>
        {error && <p role="alert" className="text-sm text-red-600">{error}</p>}
        <button disabled={busy} className="btn-primary w-full">
          {busy ? "Signing in…" : "Sign in"}
        </button>
        {passkeyReady && (
          <>
            <div className="flex items-center gap-3" aria-hidden="true">
              <span className="h-px flex-1 bg-edge-soft" />
              <span className="text-xs text-faint uppercase tracking-wide">or</span>
              <span className="h-px flex-1 bg-edge-soft" />
            </div>
            <button type="button" onClick={passkeySignIn} disabled={busy}
              className="btn-secondary w-full flex items-center justify-center gap-2">
              <KeyRound className="h-4 w-4" aria-hidden="true" />
              Sign in with a passkey
            </button>
          </>
        )}
        <p className="text-sm text-mut text-center">
          New provider? <Link to="/register" className="text-teal-700 font-medium hover:underline">Register your organisation</Link>
        </p>
      </form>
    </AuthShell>
  );
}
